import { useMutation } from '@tanstack/react-query';
import { useRef, useState, type ReactNode } from 'react';
import { cn, Icon, NameAvatar, Spinner } from '@tutero/ui';

import { useRefreshSession, useStaff } from '../session';

import {
  MAX_PICTURE_BYTES,
  PICTURE_ACCEPT,
  reasonFor,
  refusalOf,
  updateAccountPicture,
  uploadPicture,
} from './picture';

/** Which step a save has reached, which is the whole of the progress there is to show (D10). */
type Step = 'preparing' | 'sending' | 'saving';

const SAYS: Record<Step, string> = {
  preparing: 'Preparing the upload…',
  sending: 'Sending the picture…',
  saving: 'Saving it to your account…',
};

/** The cap as the ⓘ beside the name says it. */
export const PICTURE_MEGABYTES = Math.round(MAX_PICTURE_BYTES / (1024 * 1024));

/**
 * The staff member's photo (`a-staff-member-has-a-face` D1): the picture they are drawn with,
 * chosen here and stored on their account — beside the name at the head of Settings, where the
 * Flutter admin had it as a row of its own on the avatar menu. The face is its own button: a
 * hover or a keyboard's focus lays the upload arrow over it. `children` is what stands beside
 * it. A surface of the shell, not a tool — every staff member has a face, so there is no grant
 * that tells one from another (D12).
 *
 * The Flutter original picks a file, uploads it to Firebase Storage from the browser and
 * writes the `users/{id}` document from the browser too
 * (`admin-app/lib/legacy/shared/widgets/dropdowns/avatar_menu.dart:60-71`); both halves are
 * A2 violations and neither is ported. The bytes go to the address the graph minted for
 * them, and the URL lands on the account through the auth Worker (D2, D3, D4).
 */
export function ProfilePictureSection({ children }: { children: ReactNode }) {
  const staff = useStaff();
  const refreshSession = useRefreshSession();
  const input = useRef<HTMLInputElement>(null);
  const [refused, setRefused] = useState<string | null>(null);
  const [step, setStep] = useState<Step | null>(null);

  /* One write, settling the shell's session before it reports: the menu's face follows (D9, D12). */
  const save = useMutation({
    mutationFn: async (file: File) => {
      setStep('preparing');
      const url = await uploadPicture(file, staff.image, () =>
        setStep('sending'),
      );
      setStep('saving');
      await updateAccountPicture(url);
    },
    onMutate: () => setRefused(null),
    onSuccess: () => refreshSession(),
    onError: (error) => setRefused(reasonFor(error)),
    onSettled: () => setStep(null),
  });

  const remove = useMutation({
    mutationFn: () => updateAccountPicture(null),
    onMutate: () => setRefused(null),
    onSuccess: () => refreshSession(),
    onError: (error) => setRefused(reasonFor(error)),
  });

  const busy = save.isPending || remove.isPending;

  const chosen = (file: File | undefined) => {
    if (!file) return;
    const no = refusalOf(file);
    if (no !== null) {
      setRefused(no);
      return;
    }
    save.mutate(file);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-end gap-4">
        <div role="group" aria-label="Your photo" className="relative shrink-0">
          <button
            type="button"
            aria-label={staff.image ? 'Replace Picture' : 'Choose a Picture'}
            disabled={busy}
            onClick={() => input.current?.click()}
            className="group focus-visible:ring-ring/50 relative block cursor-pointer rounded-full outline-none focus-visible:ring-[3px] disabled:cursor-default"
          >
            {/* The design system's own 64px box for an account's picture; the initial stands in (D6). */}
            <NameAvatar name={staff.name} size="profile" src={staff.image} />
            <span
              aria-hidden
              className={cn(
                'bg-foreground/40 absolute inset-0 flex items-center justify-center rounded-full text-white transition-opacity',
                busy
                  ? 'opacity-100'
                  : 'opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100',
              )}
            >
              {busy ? (
                <Spinner aria-hidden className="w-5" />
              ) : (
                <Icon name="upload" size={24} />
              )}
            </span>
          </button>
          {staff.image ? (
            <button
              type="button"
              aria-label="Remove"
              disabled={busy}
              onClick={() => remove.mutate()}
              className="bg-background text-muted-foreground hover:text-destructive focus-visible:ring-ring/50 shadow-1 absolute -top-1 -right-1 flex size-6 cursor-pointer items-center justify-center rounded-full outline-none focus-visible:ring-[3px] disabled:cursor-default disabled:opacity-50"
            >
              <Icon name="close" size={14} />
            </button>
          ) : null}
        </div>
        <div className="min-w-0 flex-1">{children}</div>
      </div>
      {step === null ? null : (
        <p
          role="status"
          className="text-muted-foreground flex items-center gap-2 text-regular-s"
        >
          <Spinner aria-hidden className="w-5" />
          {SAYS[step]}
        </p>
      )}
      {refused === null ? null : (
        <p role="alert" className="text-destructive text-regular-s">
          {refused}
        </p>
      )}
      <input
        ref={input}
        type="file"
        accept={PICTURE_ACCEPT}
        aria-label="Choose a profile picture"
        className="hidden"
        tabIndex={-1}
        onChange={(event) => {
          const file = event.target.files?.[0];
          /* Cleared, so choosing the same file twice still fires a second change. */
          event.target.value = '';
          chosen(file);
        }}
      />
    </div>
  );
}
